import { del, get, set } from 'idb-keyval';
import { CustomerJourneyQuestionState } from 'src/easyclaim.app.services/CustomerJourneyQuestionState';

import { AppParameters } from '../AppParameters';
import {
	ApiV2DocumentPutRequest,
	DocumentApi,
} from '../easyclaim.api.client.typescript/src/apis/DocumentApi';
import {
	ApiV1ApprovePostRequest,
	ApiV1CaseAddFeedbackPostRequest,
	ApiV1CaseCustomerJourneyQuestionsAnswersPutRequest,
	ApiV1CaseDocumentsFinishUploadPostRequest,
	ApiV1DisapprovePostRequest,
	ApiV1LogoAnyGetRequest,
	ApiV1SGetRequest,
	ApiV2CaseCaseIdDamagePatchRequest,
	ApiV2CaseCaseIdEventsPostRequest,
	ApiV2CaseCaseIdScreensScreenIdControlsControlIdGetRequest,
	ApiV2CaseCaseIdScreensScreenIdControlsControlIdPostRequest,
	BaseResponse,
	CalculationApi,
	CaseApi,
	CaseQuestion,
	CaseUpdateControlCommandResult,
	CheckPointApi,
	Configuration,
	ControlConfiguration,
	DocumentAddCommandResult,
	EasyClaimAppState,
	EasyClaimCaseEventType,
	EasyClaimDocumentDto,
	FeedbackType,
	ImageApi,
	Language,
	LinkApi,
	PutDocumentRequest,
	SmartClaimsApi,
} from '../easyclaim.api.client.typescript/src/index';
import * as runtime from '../easyclaim.api.client.typescript/src/runtime';
import { getEasyClaimAuthorizationHeaders } from '../easyclaim.app.configuration/ciapiAuthorization';
import { messages } from '../easyclaim.app.internationalization/index';
import { reportError } from '../easyclaim.app.libs/reportError';

import { ServiceErrorAdapter } from './EasyClaimServiceErrorAdapter';

export class EasyClaimService {
	private readonly configuration: Configuration;
	private readonly appParameters: AppParameters;
	private readonly apiKey: string;

	constructor(basePath: string, apiKey: string, appParameters: AppParameters) {
		this.appParameters = appParameters;

		this.configuration = new Configuration({
			basePath: basePath,
			headers: { 'X-ApiKey': apiKey, ...getEasyClaimAuthorizationHeaders() },
		});
		this.apiKey = apiKey;
	}

	private screenControlCacheKey(caseId: string, screenId: string, controlId: string): string {
		return `sc-${caseId}-${screenId}-${controlId}`;
	}

	public async invalidateScreenControlCache(
		caseId: string,
		screenId: string,
		controlId: string
	): Promise<void> {
		await del(this.screenControlCacheKey(caseId, screenId, controlId));
	}

	@ServiceErrorAdapter()
	public getCaseByShortLinkId(shortLinkId: string) {
		const appParameters = this.appParameters;
		const resultLanguage = this.determineLanguage(appParameters);
		const request: ApiV1SGetRequest = { i: shortLinkId, l: resultLanguage };

		return new LinkApi(this.configuration).apiV1SGet(request);
	}

	@ServiceErrorAdapter()
	public setPrivacyDataAccepted(caseId: string) {
		return new CheckPointApi(this.configuration).apiV1SetPrivacyDataAcceptedDateGet({ caseId });
	}

	@ServiceErrorAdapter()
	public confirmPicturesUploaded(caseId: string): Promise<BaseResponse> {
		const request: ApiV1CaseDocumentsFinishUploadPostRequest = {
			tryFinishDocumentUploadRequest: {
				caseId: caseId,
			},
		};
		const result = new CaseApi(this.configuration).apiV1CaseDocumentsFinishUploadPost(request);

		return result;
	}

	@ServiceErrorAdapter()
	public async sendFeedback(
		caseId: string,
		feedbackComment?: string,
		feedbackRating?: number,
		feedbackType?: FeedbackType
	): Promise<void> {
		const request: ApiV1CaseAddFeedbackPostRequest = {
			caseId: caseId,
			feedbackComment: feedbackComment,
			feedbackRating: feedbackRating,
			feedbackType: feedbackType,
		};
		const result = await new CaseApi(this.configuration).apiV1CaseAddFeedbackPost(request);

		return;
	}

	@ServiceErrorAdapter()
	public async updateDamage(request: ApiV2CaseCaseIdDamagePatchRequest): Promise<void> {
		const result = await new CaseApi(this.configuration).apiV2CaseCaseIdDamagePatch(request);

		return;
	}

	@ServiceErrorAdapter()
	public updateScreenControl(
		caseId: string,
		screenId: EasyClaimAppState,
		controlId: string,
		value: string | null = null
	): Promise<CaseUpdateControlCommandResult | null> {
		const appParameters = this.appParameters;
		const resultLanguage = this.determineLanguage(appParameters);
		const request: ApiV2CaseCaseIdScreensScreenIdControlsControlIdPostRequest = {
			caseId: caseId,
			screenId: screenId,
			controlId: controlId,
			language: resultLanguage,
			caseUpdateControlCommandParams: { value: value },
		};

		return new CaseApi(this.configuration).apiV2CaseCaseIdScreensScreenIdControlsControlIdPost(
			request
		);
	}

	@ServiceErrorAdapter()
	public async getScreenControl(
		caseId: string,
		screenId: EasyClaimAppState,
		controlId: string
	): Promise<ControlConfiguration | null> {
		const cacheKey = this.screenControlCacheKey(caseId, screenId, controlId);
		const cached = await get<ControlConfiguration>(cacheKey);

		if (cached != null) return cached;

		const appParameters = this.appParameters;
		const resultLanguage = this.determineLanguage(appParameters);
		const request: ApiV2CaseCaseIdScreensScreenIdControlsControlIdGetRequest = {
			caseId: caseId,
			screenId: screenId,
			controlId: controlId,
			language: resultLanguage,
		};

		const result = await new CaseApi(
			this.configuration
		).apiV2CaseCaseIdScreensScreenIdControlsControlIdGet(request);

		if (result != null) await set(cacheKey, result);

		return result;
	}

	@ServiceErrorAdapter()
	public putDocument(
		caseId: string,
		easyClaimDocument: EasyClaimDocumentDto,
		progressCallback: (n: number | null) => void,
		analyzingCallback: (isAnalyzing: boolean) => void
	): Promise<DocumentAddCommandResult> {
		const apiKey = this.apiKey;
		const promise1 = new Promise<DocumentAddCommandResult>((resolve, reject) => {
			const appParameters = this.appParameters;
			const resultLanguage = this.determineLanguage(appParameters);
			const documentPutCommandParams: PutDocumentRequest = {
				caseId: caseId,
				documentType: easyClaimDocument.documentType,
				fileContent: easyClaimDocument.fileContent,
				fileName: easyClaimDocument.fileName,
				resultLanguage: resultLanguage,
				documentId: easyClaimDocument.documentId,
			};

			const requestParameters: ApiV2DocumentPutRequest = {
				caseId,
				putDocumentRequest: documentPutCommandParams,
			};

			if (requestParameters.caseId === null || requestParameters.caseId === undefined) {
				throw new runtime.RequiredError(
					'caseId',
					'Required parameter requestParameters.caseId was null or undefined when calling apiV2DocumentPut.'
				);
			}

			if (
				requestParameters.putDocumentRequest === null ||
				requestParameters.putDocumentRequest === undefined
			) {
				throw new runtime.RequiredError(
					'PutDocumentRequest',
					'Required parameter requestParameters.documentAddCommandParams was null or undefined when calling apiV2DocumentPut.'
				);
			}
			const xhr = new XMLHttpRequest();

			xhr.upload.onprogress = function (e) {
				if (e.lengthComputable) {
					const complete = ((e.loaded / e.total) * 100) | 0;

					if (complete >= 100.0) {
						progressCallback(complete);
						analyzingCallback(true);
					} else {
						progressCallback(complete);
					}
				}
			};

			xhr.onreadystatechange = function () {
				// In local files, status is 0 upon success in Mozilla Firefox
				if (xhr.readyState === XMLHttpRequest.DONE) {
					analyzingCallback(false);
					if (!!xhr.status && (xhr.status === 422 || xhr.status === 200)) {
						const response = JSON.parse(xhr.responseText) as DocumentAddCommandResult;

						resolve(response);
					} else {
						reject(new Error(`Document upload failed with status ${xhr.status}`));
					}
				}
			};

			xhr.onerror = function (e) {
				reportError(e, { context: 'EasyClaimService.putDocument', caseId });
				if (!!xhr.status && xhr.status === 422) {
					const response = JSON.parse(xhr.responseText) as DocumentAddCommandResult;

					resolve(response);
				} else {
					reject(new Error(`Document upload failed with status ${xhr.status}`));
				}
			};

			xhr.open('PUT', `${this.configuration.basePath}/api/v2/document?caseId=${caseId}`);
			xhr.setRequestHeader('Content-Type', 'application/json');
			xhr.setRequestHeader('X-ApiKey', apiKey);
			const authorization = getEasyClaimAuthorizationHeaders().Authorization;

			if (authorization) xhr.setRequestHeader('Authorization', authorization);

			xhr.send(JSON.stringify(documentPutCommandParams));
		});

		return promise1;
	}

	@ServiceErrorAdapter()
	public deleteDocument(caseId: string, documentId: string) {
		return new DocumentApi(this.configuration).apiV2DocumentDelete({ caseId, documentId });
	}

	@ServiceErrorAdapter()
	public approve(easyClaimApprovalRequest: ApiV1ApprovePostRequest) {
		return new SmartClaimsApi(this.configuration).apiV1ApprovePost(easyClaimApprovalRequest);
	}

	@ServiceErrorAdapter()
	public disapprove(easyClaimDisapprovalRequest: ApiV1DisapprovePostRequest) {
		return new SmartClaimsApi(this.configuration).apiV1DisapprovePost(easyClaimDisapprovalRequest);
	}

	@ServiceErrorAdapter()
	public getCalculation(caseId: string) {
		return new CalculationApi(this.configuration).apiV1CalculationGet({ caseId });
	}

	public saveBlockedByWrongDeviceEvent(caseId: string, additionalData: string | null = null) {
		return this.saveEvent(
			caseId,
			EasyClaimCaseEventType.UsingFrontendByDesktopBrowser,
			additionalData
		);
	}

	public saveStartCustomerJourneyEvent(caseId: string, additionalData: string | null = null) {
		return this.saveEvent(caseId, EasyClaimCaseEventType.StartCustomerJourney, additionalData);
	}

	@ServiceErrorAdapter()
	public saveEvent(
		caseId: string,
		eventType: EasyClaimCaseEventType,
		additionalData: string | null = null
	) {
		const request: ApiV2CaseCaseIdEventsPostRequest = {
			caseId: caseId,
			saveCaseEventRequest: {
				caseId: caseId,
				additionalData: additionalData,
				eventType: eventType,
			},
		};

		return new CaseApi(this.configuration).apiV2CaseCaseIdEventsPost(request);
	}

	public getLocalizedConnectionError(): {
		connectionErrorTitle: string;
		connectionErrorDescription: string;
	} {
		const appParameters = this.appParameters;
		const lang = appParameters.lang;
		const connectionErrorTitle = messages[lang]['connectionError.title'] || 'connectionError.title';
		const connectionErrorDescription =
			messages[lang]['connectionError.description'] || 'connectionError.description';

		return {
			connectionErrorTitle,
			connectionErrorDescription,
		};
	}

	@ServiceErrorAdapter()
	public sendCjQuestionsAnswers(caseId: string, answers: CustomerJourneyQuestionState[]) {
		const questions: CaseQuestion[] = answers.map((q) => ({
			caseId: caseId,
			customerJourneyQuestion: q.question,
			isRequired: q.required,
			answer: q.answer,
		}));

		const request: ApiV1CaseCustomerJourneyQuestionsAnswersPutRequest = {
			customerJourneyQuestionsAnswersRequest: { questions },
		};

		return new CaseApi(this.configuration).apiV1CaseCustomerJourneyQuestionsAnswersPutRaw(request);
	}

	@ServiceErrorAdapter()
	public getLogoAny(caseId?: string, customerId?: string) {
		const request: ApiV1LogoAnyGetRequest = {
			caseId: caseId,
			customerId: customerId,
		};

		return new ImageApi(this.configuration).apiV1LogoAnyGet(request);
	}

	private determineLanguage(appParameters: AppParameters): Language {
		return Language[appParameters.lang.toUpperCase()] ?? Language.EN;
	}
}
